// ChatUI.tsx
import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { TextField, Button, Typography, Box, Paper, Link } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
// @ts-ignore
import ReactMarkdown from 'react-markdown';
import { colors, fetchThemeColors } from './theme/colors';
import './typingIndicator.css'; // We'll create this CSS file next

// API base URL - should be configurable based on environment
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Initialize socket with null - we'll connect it when we have the client GUID
let socket: any = null;

// Define message type
interface Message {
  sender: 'user' | 'bot';
  text: string;
}


// Define basic types for our component props
interface ComponentProps {
  children?: React.ReactNode;
  [key: string]: any;
}

const ChatUI: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [themeColors, setThemeColors] = useState(colors);
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [clientGuid, setClientGuid] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Extract client GUID and theme colors from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guid = params.get('clientGuid');
    const themeColorsParam = params.get('themeColors');
    
    if (guid) {
      setClientGuid(guid);
      console.log('Client GUID found in chat UI:', guid);
    } else {
      console.warn('No client GUID provided in URL parameters');
    }

    // Extract theme colors from URL if available
    if (themeColorsParam) {
      try {
        const decodedThemeColors = JSON.parse(decodeURIComponent(themeColorsParam));
        setThemeColors(decodedThemeColors);
        console.log('Theme colors received from parent:', decodedThemeColors);
      } catch (error) {
        console.error('Failed to parse theme colors from URL:', error);
        // Fallback to default colors
        setThemeColors(colors);
      }
    }
  }, []);

  // Initialize socket connection with client GUID when available
  useEffect(() => {
    if (clientGuid) {
      // Initialize the socket with client GUID
      console.log('Initializing socket with client GUID:', clientGuid);
      socket = io(API_BASE_URL, {
        transports: ['websocket'],
        query: {
          clientId: clientGuid  // Send clientGuid as clientId in the handshake query
        }
      });

      // Set up socket event listeners
      setupSocketListeners();

      // Clean up on unmount
      return () => {
        if (socket) {
          socket.disconnect();
        }
      };
    }
  }, [clientGuid]);

  // Add event listener to detect when user closes or navigates away from the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (socket) {
        console.log('User leaving page, disconnecting socket');
        socket.disconnect();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Initialize chat and scroll to bottom when component mounts
  useEffect(() => {
    scrollToBottom();
  }, []);

  // Set up socket event listeners
  const setupSocketListeners = () => {
    if (!socket) return;

    // Track socket connection status
    socket.on('connect', () => {
      setIsConnected(true);
      console.log('Connected to server with client GUID:', clientGuid);
    });
    
    socket.on('disconnect', () => {
      setIsConnected(false);
      // If typing indicator was active, cancel it when connection is lost
      setIsTyping(false);
      console.log('Disconnected from server');
    });
    
    socket.on('connect_error', (error: any) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
      // If there was a connection error, show a message only if we were expecting a response
      if (isTyping) {
        setIsTyping(false);
        setMessages(prev => [...prev, { 
          sender: 'bot', 
          text: 'Connection error. Please check your internet connection and refresh the page.' 
        }]);
      }
    });

    socket.on('bot message', (msg: string) => {
      setIsTyping(false);
      setMessages((prev) => [...prev, { sender: 'bot', text: msg }]);
    });

    socket.on('error', (error: string) => {
      console.error('Socket error message:', error);
      setIsTyping(false);
      setMessages(prev => [...prev, { 
        sender: 'bot', 
        text: `Error: ${error}. Please try again or contact support.` 
      }]);
    });
  };

  // Handle typing indicator timeout
  useEffect(() => {
    let typingTimeout: NodeJS.Timeout;
    
    if (isTyping) {
      // Set a timeout to hide the typing indicator if no response is received within 15 seconds
      typingTimeout = setTimeout(() => {
        setIsTyping(false);
        if (isConnected) {
          setMessages(prev => [...prev, { 
            sender: 'bot', 
            text: 'Sorry, I seem to be having trouble connecting. Please try again or refresh the page.' 
          }]);
        }
      }, 100000);
    }
    
    return () => {
      clearTimeout(typingTimeout);
    };
  }, [isTyping, isConnected]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = () => {
    if (!input.trim() || !socket) return;
    const newMessage: Message = { sender: 'user', text: input };
    setMessages((prev) => [...prev, newMessage]);
    
    if (isConnected) {
      setIsTyping(true);
      socket.emit('chat message', input, (acknowledgment: any) => {
        // If the backend acknowledges receipt but doesn't respond, 
        // this can help detect if backend processing started
        console.log('Message received by server:', acknowledgment);
      });
    } else {
      // If not connected, show an error message
      setMessages(prev => [...prev, { 
        sender: 'bot', 
        text: 'Unable to send message. Please check your connection and try again.' 
      }]);
    }
    
    setInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSend();
  };

  // Typing indicator component
  const TypingIndicator = () => (
    <Box
      sx={{
        alignSelf: 'flex-start',
        bgcolor: themeColors.botBubble,
        color: themeColors.botText,
        px: 2,
        py: 1,
        borderRadius: '18px 18px 18px 0',
        maxWidth: '70%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        minWidth: '60px',
        minHeight: '32px'
      }}
    >
      <div className="typing-dots-container">
        <span className="typing-dot dot-1">•</span>
        <span className="typing-dot dot-2">•</span>
        <span className="typing-dot dot-3">•</span>
      </div>
    </Box>
  );

  return (
    <div>
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          background: themeColors.background,
          borderRadius: 0,
          overflow: 'hidden',
        }}
      >
        {/* Chat messages area */}
        <Box 
          ref={chatContainerRef}
          sx={{ 
            flex: 1,
            p: 3,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            bgcolor: themeColors.background,
          }}
        >
          {
            messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  bgcolor: msg.sender === 'user' ? themeColors.userBubble : themeColors.botBubble,
                  color: msg.sender === 'user' ? themeColors.userText : themeColors.botText,
                  px: 2,
                  py: 1,
                  borderRadius: msg.sender === 'user' ? '18px 18px 0 18px' : '18px 18px 18px 0',
                  maxWidth: msg.sender === 'user' ? '60%' : '70%',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                
                  <Typography sx={{ 
                    overflowWrap: 'break-word', 
                    wordBreak: 'break-word',
                    fontSize: '0.9rem',
                    lineHeight: 1.3,
                    mb: 0
                  }}>
                    <ReactMarkdown components={{
                      a: ({ node, ...props }) => (
                        <Link 
                          href={props.href} 
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            color: msg.sender === 'user' ? themeColors.userLink : themeColors.botLink,
                            textDecoration: 'underline',
                            '&:hover': {
                              textDecoration: 'underline',
                              opacity: 0.8
                            }
                          }}
                        >
                          {props.children}
                        </Link>
                      ),
                      img: ({ node, ...props }) => (
                        <img 
                          src={props.src} 
                          alt={props.alt} 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            width: 'auto', 
                            height: 'auto',
                            objectFit: 'contain',
                            margin: '8px 0'
                          }} 
                        />
                      ),
                      p: ({ node, ...props }) => (
                        <span {...props} />
                      )
                    }}>
                      {msg.text}
                    </ReactMarkdown>
                  </Typography>
              </Box>
            ))
          }
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </Box>
      </Paper>
        {/* Fixed input area */}
        <Box 
          sx={{ 
            p: 1, 
            borderTop: `1px solid ${themeColors.border}`,
            bgcolor: themeColors.background,
            display: 'block',
            alignItems: 'center',
            position: 'sticky',
            bottom: 0,
          }}
        >
          <div style={{flex: 1, display: 'flex', alignItems: 'center'}}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            multiline
            minRows={1}
            maxRows={3}
            size="small"
            sx={{
              flex: 1,
              m: 0,
              mt: '10px',
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                pl: 0.5,
                backgroundColor: themeColors.inputBackground,
                fontSize: '0.9rem',
                '& fieldset': {
                  borderColor: themeColors.botText,
                },
              },
              '& .MuiInputBase-input': {
                color: themeColors.botText,
                py: 0.75,
                px: 1.5,
                pl: 1,
                overflow: 'auto',
                maxHeight: '60px'
              },
            }}
          />
          <Button 
            variant="contained" 
            onClick={handleSend} 
            sx={{
              m:0,
              mt: '10px',
              ml: 1,
              px: 2,
              py: 0,
              height: '32px',
              width: '80px',
              minWidth: 'unset',
              alignSelf: 'center',
              bgcolor: themeColors.userBubble,
              color: themeColors.userText,
              borderRadius: 0,
              fontSize: '0.85rem',
              '&:hover': {
                bgcolor: themeColors.buttonHover,
              }
            }}

          >
            SEND
          </Button>
          </div>
          <div style={{textAlign: 'center', fontSize: '0.8rem', color: themeColors.botText}}>
            Powered by TechuNafa    
          </div>
        </Box>
    </div>
  );
};

export default ChatUI;
